import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Btn, Field, Input, Badge } from "@/components/erp/ui";
import { toast } from "sonner";
import { Check, X, UserCheck } from "lucide-react";

const F = [["window_months", "Periode dinilai (bulan terakhir)"], ["min_tenure_months", "Masa kerja minimal (bulan)"], ["min_perf", "Rata-rata rapor minimal"],
  ["max_late_att_pct", "Maks. telat masuk (% hari hadir)"], ["max_late_stage_pct", "Maks. tahap WO telat (%)"], ["convert_days", "Konsultasi dihitung jadi quotation dalam (hari)"]];

const Rules = ({ r, onSaved, readOnly }) => {
  const [v, setV] = useState(r);
  useEffect(() => { setV(r); }, [r]);
  const save = async () => { try { await api.put("/konsultan/rules", v); toast.success("Syarat Konsultan 3D disimpan"); onSaved && onSaved(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } };
  if (readOnly) {
    return (
      <div className="mb-4" data-testid="kons-rules-view">
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {F.map(([k, l]) => <div key={k} className="border border-slate-200 rounded-lg px-3 py-2"><div className="text-[10px] font-mono uppercase text-slate-500">{l}</div><div className="font-semibold" data-testid={`kons-rule-view-${k}`}>{r[k]}</div></div>)}
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Syarat hanya bisa diubah admin utama di Settings → Konsultan 3D.</p>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end mb-4" data-testid="kons-rules">
      {F.map(([k, l]) => <Field key={k} label={l}><Input type="number" data-testid={`kons-rule-${k}`} value={v[k] ?? ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} /></Field>)}
      <Btn data-testid="kons-rules-save" onClick={save}>Simpan syarat</Btn>
    </div>
  );
};

// Settings: syarat Konsultan 3D, hanya admin utama yang bisa mengubah
export const KonsultanRulesSettings = ({ owner }) => {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/konsultan/rules").then(({ data }) => setR(data)).catch(() => {}); }, []);
  if (!r) return null;
  return (
    <Card className="p-5" data-testid="kons-rules-settings">
      <div className="font-semibold mb-1">Syarat Konsultan 3D</div>
      <p className="text-xs text-slate-500 mb-4">Dipakai untuk menandai designer yang memenuhi syarat di Dashboard Design → Konsultan 3D, dan batas hari konsultasi dihitung jadi quotation untuk bonus.</p>
      <Rules r={r} readOnly={!owner} onSaved={() => api.get("/konsultan/rules").then(({ data }) => setR(data))} />
    </Card>
  );
};

// Owner: daftar designer + syarat terukur untuk menjadi Konsultan 3D
export const KonsultanPanel = () => {
  const [d, setD] = useState(null);
  const load = () => { api.get("/konsultan/candidates").then(({ data }) => setD(data)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const toggle = async (it) => {
    const on = !it.active;
    if (on && !it.eligible && !window.confirm(`${it.name} belum memenuhi semua syarat. Tetap aktifkan sebagai Konsultan 3D?`)) return;
    try { await api.post("/konsultan/activate", { email: it.email, active: on, forced: on && !it.eligible }); toast.success(on ? `${it.name} aktif sebagai Konsultan 3D` : `Konsultan 3D ${it.name} dinonaktifkan`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  if (!d) return null;
  return (
    <Card className="p-5 mt-6" data-testid="konsultan-panel">
      <div className="flex items-center gap-2 font-semibold mb-1"><UserCheck className="w-4 h-4" /> Konsultan 3D</div>
      <p className="text-xs text-slate-500 mb-4">Konsultan 3D menerima tamu konsultasi di workshop (menu Booking, Quotation, Customers). Designer yang memenuhi semua syarat ditandai, owner yang mengaktifkan. Dinilai {d.months[0]} s/d {d.months[d.months.length - 1]}.</p>
      <Rules r={d.rules} readOnly />
      <div className="space-y-2">
        {d.items.map((it) => (
          <div key={it.employee_id} className="border border-slate-200 rounded-lg p-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`kons-row-${it.employee_id}`}>
            <div className="md:w-48">
              <div className="font-medium text-sm">{it.name}</div>
              <div className="flex gap-1 mt-1">{it.eligible ? <Badge>MEMENUHI SYARAT</Badge> : <span className="text-[10px] text-slate-400">Belum memenuhi</span>}{it.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-600 text-white" data-testid={`kons-active-${it.employee_id}`}>Konsultan 3D</span>}</div>
            </div>
            <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-1 text-xs">
              {it.checks.map((c) => <div key={c.key} className={c.ok ? "text-emerald-700" : "text-rose-600"} data-testid={`kons-check-${it.employee_id}-${c.key}`}>{c.ok ? <Check className="w-3 h-3 inline" /> : <X className="w-3 h-3 inline" />} {c.label}: <b>{c.value}</b></div>)}
            </div>
            {it.dedicated ? <span className="text-xs text-slate-400">Role utama</span> : !it.has_account ? <span className="text-xs text-slate-400">Belum punya akun</span> :
              <Btn variant={it.active ? "ghost" : "primary"} data-testid={`kons-toggle-${it.employee_id}`} onClick={() => toggle(it)}>{it.active ? "Nonaktifkan" : "Aktifkan"}</Btn>}
          </div>
        ))}
        {!d.items.length && <p className="text-sm text-slate-400">Belum ada designer.</p>}
      </div>
    </Card>
  );
};
